import {
  faExpand,
  faInfoCircle,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  CustomInput,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import { getPropertyStalls, updateStall } from "../../helpers/api/parking";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import FalconCardHeader from "../common/FalconCardHeader";
import Flex from "../common/Flex";
import PageHeader from "../common/PageHeader";
import FullPagination from "../common/pagination/FullPagination";
import {
  If,
  isIterableArray,
  Message,
  Or,
  PreloadState,
} from "../UI/preloading/PreloadState";
import { Toast } from "../UI/toast/Toast";

const MyStalls = () => {
  const { id } = useParams();
  const history = useHistory();
  const [isAvailable, setIsAvailable] = useState(true);
  const [params] = useState({
    parking: id,
    search: "",
    available: undefined,
  });
  const { role } = useSelector((state) => state.auth.user);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [selectedStall, setSelectedStall] = useState(null);
  const [plateNumber, setPlateNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const {
    data,
    error,
    isLoading,
    mutate,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(getPropertyStalls, params);

  const vacate = async (id, state) => {
    setSubmitting(true);
    try {
      const res = await updateStall(id, state);
      if (res.code === SUCCESS_RESPONSE.OK) {
        mutate();
        Toast.fire({
          type: "success",
          title: `Puesto ${
            !!state.available ? "Desocupado" : "Ocupado"
          } Satisfactoriamente.`,
        });
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
      setPlateNumber(null);
      setSelectedStall(null);
    }
  };

  const handleModalOcuped = () => (
    <Modal className="modal-lg" isOpen={collapseOne} data-testid="modal">
      <ModalHeader>
        <h5>Ocupación de puesto</h5>
      </ModalHeader>
      <ModalBody className="bg-light">
        <Row>
          <Col tag={FormGroup}>
            <Label>Placa o identificación del vehículo</Label>
            <Input
              minLength={4}
              type="text"
              value={plateNumber}
              onChange={({ target }) =>
                setPlateNumber(target.value.toUpperCase())
              }
              required
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={() => collapseOneOpen(false)}>
          Cancelar
        </Button>
        <Button
          data-testid="submit-button"
          color="primary"
          type="submit"
          onClick={() => {
            vacate(selectedStall, { available: 0, description: plateNumber });
          }}
          disabled={!plateNumber || submitting}
        >
          Ocupar puesto {submitting && <Spinner size="sm" color="white" />}
        </Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <>
      <PageHeader
        title="Puestos"
        titleTag="h2"
        description={
          "Si necesitas ayuda comunicanos, nuestra finalidad es ayudar a los usuarios para que puedan resolver inconvenientes."
        }
        className="mb-3"
        col={{ lg: 9 }}
      />
      <Card>
        <FalconCardHeader title="Mis puestos">
          <Row>
            <Col className="d-flex my-0 justify-content-end">
              {isAvailable ? "Puestos disponibles" : "Puestos no disponibles"}
              <CustomInput
                style={{ cursor: "pointer" }}
                className="ml-2"
                type="switch"
                id="enabled"
                checked={isAvailable}
                onChange={() => setIsAvailable(!isAvailable)}
              />
            </Col>
          </Row>
        </FalconCardHeader>
        <CardBody
          className={classNames("fs--1", { "p-0": isIterableArray(data) })}
        >
          <PreloadState
            loading={isLoading || submitting}
            message={submitting ? "Actualizando puesto..." : ""}
            cond={isIterableArray(data)}
          >
            <Or value={true}>
              <Table className="mb-0" responsive>
                <thead>
                  <tr>
                    <th width="10%" className="align-middle text-center">
                      Puesto
                    </th>
                    <th width="30%" className="align-middle text-center">
                      Estado
                    </th>
                    <th className="align-middle text-center">
                      Fecha de creación
                    </th>
                    <If cond={["property", "safety"]}>
                      <th
                        value={role}
                        width="10%"
                        className="align-middle text-center"
                      >
                        Opciones
                      </th>
                    </If>
                  </tr>
                </thead>
                <tbody>
                  {data.map(
                    ({
                      available,
                      created_at,
                      description,
                      property_parking_id,
                      acronym,
                    }) =>
                      !!available === isAvailable && (
                        <tr key={property_parking_id}>
                          <td className="align-middle">
                            <Flex
                              className="flex-column"
                              align="center"
                              justify="center"
                            >
                              {<b>{`${acronym}-${property_parking_id}`}</b>}
                            </Flex>
                          </td>
                          <td className="align-middle text-center">
                            <div>
                              {available ? (
                                <Badge color="soft-success" pill>
                                  Disponible
                                </Badge>
                              ) : (
                                <Badge color="soft-danger" pill>
                                  Ocupado por {description}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="align-middle text-center">
                            {moment(created_at).format("DD/MM/YYYY")}
                          </td>
                          <If cond={["property", "safety"]}>
                            <td value={role} className="text-center">
                              <UncontrolledDropdown
                                direction="left"
                                className="d-inline-block ml-2"
                              >
                                <DropdownToggle
                                  color="falcon-default"
                                  size="sm"
                                >
                                  <FontAwesomeIcon icon="ellipsis-h" />
                                </DropdownToggle>
                                <DropdownMenu right>
                                  <DropdownItem
                                    onClick={() =>
                                      history.push(
                                        `/dashboard/parking/my/stalls/${property_parking_id}`
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon
                                      className="mr-2"
                                      icon={faInfoCircle}
                                    />
                                    Ver detalles
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      if (available === 1) {
                                        setSelectedStall(property_parking_id);
                                        collapseOneOpen(true);
                                      } else {
                                        vacate(property_parking_id, {
                                          available: 1,
                                        });
                                      }
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      className="mr-2"
                                      icon={!!!available ? faUndo : faExpand}
                                    />
                                    {!!!available ? "Desocupar" : "Ocupar"}
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </If>
                        </tr>
                      )
                  )}
                </tbody>
              </Table>
            </Or>
            <Or value={false} cond={!!error}>
              <Message
                className="p-3"
                value={false}
                color="info"
                icon={faInfoCircle}
              >
                No hay puestos para mostrar.
              </Message>
              <Message
                className="p-3"
                value={true}
                color="danger"
                icon={faInfoCircle}
              >
                Ha ocurrido un error.
              </Message>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && !isLoading && (
          <CardFooter className="border-top">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"puestos"}
              perPage={perPage}
            />
          </CardFooter>
        )}
        {handleModalOcuped()}
      </Card>
    </>
  );
};

export default MyStalls;
